import { useMemo, useState, type CSSProperties } from 'react'
import { Link, useParams } from 'react-router-dom'
import { CartDrawer } from '../../components/shop/CartDrawer'
import { ProductVisual } from '../../components/shop/ProductVisual'
import { ShopNav } from '../../components/shop/ShopNav'
import { useCart } from '../../context/CartContext'
import { formatCurrency, products, statusLabel } from '../../data/shop/products'
import './ShopPage.css'

const swatches:Record<string,string> = {
  Black:'#111213',
  'Midnight Navy':'#101c31',
  'Oxblood Red':'#64191f',
  'Bone White':'#e8e3d8',
}

export function ShopProductPage(){
  const {slug}=useParams()
  const product=useMemo(()=>products.find(item=>item.slug===slug),[slug])
  const cart=useCart()
  const [imageIndex,setImageIndex]=useState(0)
  const [color,setColor]=useState('Black')
  const [size,setSize]=useState<string>()
  const [quantity,setQuantity]=useState(1)
  const [error,setError]=useState('')
  const [email,setEmail]=useState('')
  const [notified,setNotified]=useState(false)

  if(!product)return <div className="shop-page"><ShopNav/><div className="shop-missing container"><span>404 / Shop</span><h1>Product not found.</h1><Link className="button" to="/shop">Return to shop →</Link></div><CartDrawer/></div>

  const colors=[...new Set(product.variants?.map(variant=>variant.color).filter(Boolean)??[])] as string[]
  const sizes=[...new Set(product.variants?.map(variant=>variant.size).filter(Boolean)??[])] as string[]
  const selectedVariant=product.variants?.find(variant=>(!variant.color||variant.color===color)&&(!variant.size||variant.size===size))
  const canBuy=product.status==='available'||product.status==='preorder'

  function chooseColor(value:string){
    setColor(value)
    const nextImage=product?.images.findIndex(image=>image.color===value)??-1
    if(nextImage>=0)setImageIndex(nextImage)
    setError('')
  }

  function add(){
    const current=product
    if(!current)return
    if(current.variants?.some(variant=>variant.size)&&!size){setError('Choose a size before adding this item.');return}
    if(!selectedVariant&&current.variants?.length){setError('That option is not available.');return}
    if(current.priceCents===undefined)return
    cart.addItem({productId:current.id,variantId:selectedVariant?.id??current.id,name:current.name,image:current.images[imageIndex]?.src,priceCents:current.priceCents,quantity,size:selectedVariant?.size,color:selectedVariant?.color})
    setError('')
  }

  function notify(event:React.FormEvent){
    event.preventDefault()
    const current=product
    if(!current||!email)return
    const values=JSON.parse(localStorage.getItem('aldapa-shop-notifications')??'[]') as {productId:string;email:string}[]
    localStorage.setItem('aldapa-shop-notifications',JSON.stringify([...values,{productId:current.id,email}]))
    setNotified(true)
  }

  return <div className="shop-page"><ShopNav/><main className="shop-detail"><div className="container">
    <div className="shop-detail__crumb"><Link to="/shop">Shop</Link><span>/</span><Link to={`/shop/${product.category}`}>{product.category}</Link><span>/</span><span>{product.name}</span></div>
    <div className="shop-detail__layout">
      <div className="shop-gallery">
        <div className={`shop-gallery__main ${product.images[imageIndex]?.color?'is-colorway':''}`}>{product.images[imageIndex]?<img src={product.images[imageIndex].src} alt={product.images[imageIndex].alt}/>:<ProductVisual product={product}/>}</div>
        {product.images.length>1&&<div className="shop-gallery__thumbs">{product.images.map((image,index)=><button className={imageIndex===index?'active':''} onClick={()=>{setImageIndex(index);if(image.color)setColor(image.color)}} key={image.src}><img src={image.src} alt=""/><span>{image.label??`View ${index+1}`}</span></button>)}</div>}
      </div>
      <section className="shop-detail__info">
        <div className="shop-detail__status"><span>{statusLabel[product.status]}</span>{product.model&&<span>{product.model}</span>}</div>
        <span className="shop-detail__collection">{product.collection??`Aldapa / ${product.category}`}</span>
        <h1>{product.name}</h1>
        <p>{product.description}</p>
        <div className="shop-purchase">
          <header className="shop-purchase__price"><span>Price</span><div><strong>{formatCurrency(product.priceCents)}</strong>{product.priceCents!==undefined&&<small>USD</small>}</div><p>Concept pricing · final production details pending</p></header>
          {colors.length>0&&<fieldset className="shop-colors"><legend>Color <strong>{color}</strong></legend><div>{colors.map(value=><button className={color===value?'selected':''} type="button" onClick={()=>chooseColor(value)} key={value} aria-label={`Select ${value}`}><i style={{'--swatch':swatches[value]??'#777'} as CSSProperties}/><span>{value}</span></button>)}</div></fieldset>}
          {sizes.length>0&&<fieldset><legend>Size <button type="button" onClick={()=>setError('Final measurements will be published after the production garment is selected.')}>Size guide →</button></legend><div className="shop-sizes">{sizes.map(value=><button className={size===value?'selected':''} type="button" onClick={()=>{setSize(value);setError('')}} key={value}>{value}</button>)}</div></fieldset>}
          <div className="shop-quantity"><span>Quantity</span><div><button type="button" aria-label="Decrease quantity" onClick={()=>setQuantity(value=>Math.max(1,value-1))}>−</button><span>{quantity}</span><button type="button" aria-label="Increase quantity" onClick={()=>setQuantity(value=>value+1)}>+</button></div></div>
          {error&&<p className="shop-form-message" role="status">{error}</p>}
          {canBuy?<button className="shop-purchase__action" type="button" onClick={add}><span>{product.status==='preorder'?'Preorder':'Add to bag'}</span><strong>{formatCurrency((product.priceCents??0)*quantity)}</strong><i>→</i></button>:<form className="shop-notify" onSubmit={notify}>{notified?<p role="status">You’re on the notification list for this product.</p>:<><label><span>Release notification</span><input required type="email" value={email} onChange={event=>setEmail(event.target.value)} placeholder="Email address"/></label><button type="submit"><span>Notify me</span><i>→</i></button></>}</form>}
          <footer><span>Secure checkout</span><span>Shipping calculated at checkout</span></footer>
        </div>
        <div className="shop-accordions">{product.details.map((detail,index)=><details key={detail.label} open={index===0}><summary>{detail.label}<span>+</span></summary><p>{detail.copy}</p></details>)}</div>
      </section>
    </div>
  </div></main><CartDrawer/></div>
}
